import { Transition } from "@headlessui/react";
import axios from "axios";
import Cookies from "js-cookie";
import {
  Pencil,
  Briefcase,
  Star,
  GraduationCap,
  Lightbulb,
  Linkedin as LinkedinIcon,
  Github,
  Globe,
  MapPin,
  User,
  File,
} from "lucide-react";
import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router";

import ProfilePic from "../../../assets/images/blank-profile-pic.png";

import Education from "./Education";
// import ContactInfo from "./ContactInfo";
import Projects from "./Projects";
import ResumeUploader from "./ResumeUploader";
import Skills from "./Skills";
import WorkExp from "./WorkExp";

export default function Contact() {
  const navigate = useNavigate();
  const { candidateId } = useParams(); // For recruiter view
  const [userData, setUserData] = useState({
    name: "",
    profile_picture: "",
    address_details: null,
  });
  const [socialData, setSocialData] = useState({
    linkedin: "",
    github: "",
    portfolio: "",
    description: "",
    summary: "",
  });
  const [activeTab, setActiveTab] = useState("workexp");
  const [applicationId, setApplicationId] = useState(null);
  const [completionData, setCompletionData] = useState({
    completion: 0,
    checklist: [],
  });
  const [isRecruiterView, setIsRecruiterView] = useState(false);

  const handleTabClick = (tab) => {
    setActiveTab(tab);
  };

  const tabs = [
    {
      id: "workexp",
      label: "Work Experience",
      icon: <Briefcase className="w-5 h-5" />,
    },
    { id: "skills", label: "Skills", icon: <Star className="w-5 h-5" /> },
    {
      id: "education",
      label: "Education",
      icon: <GraduationCap className="w-5 h-5" />,
    },
    {
      id: "projects",
      label: "Projects",
      icon: <Lightbulb className="w-5 h-5" />,
    },
    { id: "resume", label: "Resume", icon: <File className="w-5 h-5" /> },
  ];

  const tabContent = {
    workexp: <WorkExp />,
    skills: <Skills />,
    education: <Education />,
    projects: <Projects />,
    resume: <ResumeUploader />,
  };

  useEffect(() => {
    const loggedInUserId = Cookies.get("id");
    const targetUserId = candidateId || loggedInUserId;

    if (candidateId) {
      setIsRecruiterView(true);
    }

    if (!targetUserId) {
      console.error("Target User ID not found");
      return;
    }
    const fetchProfile = async () => {
      try {
        let userProfileData;
        if (isRecruiterView) {
          // For recruiters, use the dedicated candidate-profile endpoint
          const profileRes = await axios.get(
            `/api/candidate-profile/${targetUserId}/`,
            { withCredentials: true },
          );
          userProfileData = profileRes.data;
        } else {
          // For candidates viewing their own profile
          const userRes = await axios.get(`/api/users/${targetUserId}/`, {
            withCredentials: true,
          });
          userProfileData = userRes.data;
        }
        setUserData(userProfileData);

        if (
          userProfileData.applicationids &&
          userProfileData.applicationids.length > 0
        ) {
          const appId = userProfileData.applicationids[0].id;
          setApplicationId(appId);

          const appRes = await axios.get(`/api/application/${appId}/`, {
            withCredentials: true,
          });
          setSocialData(appRes.data);
        } else {
          setSocialData({
            linkedin: "",
            github: "",
            portfolio: "",
            description: "",
            summary: "",
          });
        }
      } catch (error) {
        console.error("Error fetching user data:", error);
      }
    };

    const fetchCompletion = async () => {
      if (!candidateId) {
        // Only for candidate's own view
        try {
          const res = await axios.get(
            "/api/users/dashboard/profile-completion/",
            { withCredentials: true },
          );
          setCompletionData(res.data);
        } catch (error) {
          console.error("Error fetching profile completion data:", error);
        }
      }
    };

    fetchProfile();
    fetchCompletion();
  }, [candidateId, isRecruiterView]);

  const handleEditProfile = () => {
    navigate("/setprofile", { state: { from: "edit" } });
  };

  const socialLinks = [
    { Icon: LinkedinIcon, link: socialData.linkedin, name: "LinkedIn" },
    { Icon: Github, link: socialData.github, name: "GitHub" },
    { Icon: Globe, link: socialData.portfolio, name: "Portfolio" },
  ];

  const completion = completionData.completion || 0;
  const getProgressColor = () => {
    if (completion <= 20) return "text-red-500";
    if (completion <= 60) return "text-orange-500";
    return "text-green-500";
  };

  const radius = 44; // slightly smaller than half of the container width/height (w-24 -> 96px, so radius is < 48)
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (completion / 100) * circumference;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8 px-4 sm:px-6 lg:px-8 mt-12">
      <div className="max-w-7xl mx-auto">
        {/* Profile Header */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-6">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between">
            <div className="flex items-start gap-5">
              <div className="relative w-24 h-24 flex-shrink-0 group">
                <img
                  alt="profile pic"
                  className="w-full h-full rounded-full object-cover border-4 border-white shadow-md"
                  src={userData.profile_picture || ProfilePic}
                />
                {!isRecruiterView && (
                  <>
                    <svg
                      className="absolute top-0 left-0 w-full h-full"
                      viewBox="0 0 100 100"
                    >
                      {/* Background Circle */}
                      <circle
                        className="text-gray-200 dark:text-gray-700"
                        cx="50"
                        cy="50"
                        fill="transparent"
                        r={radius}
                        stroke="currentColor"
                        strokeWidth="6"
                      />
                      {/* Progress Circle */}
                      <circle
                        className={`${getProgressColor()} transition-all duration-500 ease-in-out`}
                        cx="50"
                        cy="50"
                        fill="transparent"
                        r={radius}
                        stroke="currentColor"
                        strokeDasharray={circumference}
                        strokeDashoffset={strokeDashoffset}
                        strokeLinecap="round"
                        strokeWidth="6"
                        transform="rotate(-90 50 50)"
                      />
                    </svg>
                    <div
                      className={`absolute -bottom-2 left-1/2 -translate-x-1/2 px-2 py-0.5 rounded-full text-xs font-semibold text-white ${getProgressColor().replace("text-", "bg-")}`}
                    >
                      {completion}%
                    </div>
                  </>
                )}
              </div>

              <div>
                <h1 className="text-lg font-bold text-gray-900 dark:text-gray-100">
                  {userData.name}
                </h1>
                <div className="mt-2 flex flex-col md:flex-row gap-y-2 md:gap-x-4 items-center">
                  {socialData.description && (
                    <div className="flex items-center text-sm text-gray-500 dark:text-gray-400">
                      <User className="w-4 h-4 mr-2" />
                      <p>{socialData.description}</p>
                    </div>
                  )}
                  {userData.address_details && (
                    <div className="flex items-center text-sm text-gray-500 dark:text-gray-400">
                      <MapPin className="w-4 h-4 mr-2" />
                      <span>
                        {userData.address_details.city ||
                          userData.address_details.state_province}{" "}
                        , {userData.address_details.country}
                      </span>
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-4 mt-3">
                  {socialLinks.map(({ Icon, link, name }) =>
                    link ? (
                      <a
                        key={name}
                        aria-label={name}
                        className="text-gray-500 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                        href={link}
                        rel="noopener noreferrer"
                        target="_blank"
                      >
                        <Icon className="size-4" />
                      </a>
                    ) : null,
                  )}
                </div>
              </div>
            </div>

            {!isRecruiterView && (
              <button
                className="mt-4 sm:mt-0 flex-shrink-0 flex items-center gap-2 text-sm font-semibold cursor-pointer text-blue-700 dark:text-blue-400 hover:text-blue-500 dark:hover:text-blue-300 transition-all duration-200"
                onClick={handleEditProfile}
              >
                <Pencil className="w-4 h-4" />
                Edit Profile
              </button>
            )}
          </div>
        </div>

        {/* Summary */}
        {socialData.summary && (
          <div className="mt-6 p-6">
            <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-100 mb-3">
              Professional Summary
            </h2>
            <p className="text-gray-500 dark:text-gray-400 text-sm whitespace-pre-wrap">
              {socialData.summary}
            </p>
          </div>
        )}

        {/* Main Content */}
        <div className="mt-6 flex flex-col md:flex-row gap-6">
          {/* Left Nav */}
          <div className="md:w-1/4">
            <div className="p-4">
              <nav className="flex flex-col space-y-1">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    className={`flex items-center gap-3 w-full text-left px-4 py-3 rounded-lg font-medium text-sm transition-colors ${
                      activeTab === tab.id
                        ? "bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400"
                        : "text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-gray-100"
                    }`}
                    onClick={() => handleTabClick(tab.id)}
                  >
                    {tab.icon}
                    <span>{tab.label}</span>
                  </button>
                ))}
              </nav>
            </div>
          </div>

          {/* Right Content */}
          <div className="flex-1">
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-6 min-h-[300px]">
              <Transition
                key={activeTab}
                appear
                enter="transition-opacity duration-300"
                enterFrom="opacity-0"
                enterTo="opacity-100"
                show
              >
                {/* Wrap the tab content in a div to allow Transition to attach a ref */}
                <div>{tabContent[activeTab]}</div>
              </Transition>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
